import React, { useEffect, useState, useMemo } from 'react'
import { Card, List, Button, Typography, Space, Tag, Empty, Row, Col, App, Modal, Skeleton, Input } from 'antd'
import {
  VideoCameraOutlined,
  PictureOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  SyncOutlined,
  HistoryOutlined,
  UserAddOutlined,
  SearchOutlined,
  CloseOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import { on as onEvent } from '@/services/events'
import { apiClient } from '@/services/api'
import { getProviderName } from '@/utils/providerMapping'
import { COMMON_STYLES } from '@/styles/globalStyles'
import { useAuthStore } from '@/store/authStore'
import { motion } from 'framer-motion'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography
  // Tabs.TabPane 已弃用，改用 items

const HistoryPage: React.FC = () => {
  const { message } = App.useApp()
  const [searchParams] = useSearchParams()
  
  // 从URL参数获取初始tab值
  const initialTab = searchParams.get('type') || 'all'
  const [activeTab, setActiveTab] = useState(initialTab)
  const [filterStatus, setFilterStatus] = useState('all')
  const [dateRange, setDateRange] = useState<any[]>([])

  const [data, setData] = useState<any[]>([])
  const [videoTotal, setVideoTotal] = useState<number>(0)
  const [imageTotal, setImageTotal] = useState<number>(0)
  const [paginationTotal, setPaginationTotal] = useState<number>(0) // 当前分页的总数（用于分页组件）
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  // 🔍 关键词搜索
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set())
  const [refreshKey, setRefreshKey] = useState(0) // 用于强制刷新列表
  const [failedMediaIds, setFailedMediaIds] = useState<Set<string>>(new Set()) // 跟踪加载失败的媒体 ID
  // 是否存在进行中的任务（用于决定是否开启定时刷新）
  const [hasInProgress, setHasInProgress] = useState(false)
  // 下载状态跟踪，防止重复下载
  const [downloadingUrls, setDownloadingUrls] = useState<Set<string>>(new Set())
  
  // 视频预览Modal状态
  const [videoPreviewModal, setVideoPreviewModal] = useState({
    visible: false,
    videoUrl: '',
    prompt: ''
  })
  
  // 图片预览Modal状态
  const [imagePreviewModal, setImagePreviewModal] = useState({
    visible: false,
    imageUrls: [] as string[],
    prompt: ''
  })

  // 🔥 创建角色Modal状态
  const [createCharacterModal, setCreateCharacterModal] = useState({
    visible: false,
    videoItem: null as any,
    characterName: '',
    timestamps: '0,3',
    loading: false
  })

  // 加载真实数据（视频+图片）
  const [isLoading, setIsLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767)
  
  const loadData = async () => {
    // 避免重复加载
    if (isLoading) {
      console.log('⏸️  [历史列表] 已有加载任务进行中，跳过')
      return
    }
    
    try {
      // 只在首次加载时显示加载状态，避免切换页面时抖动
      if (data.length === 0) {
        setIsLoading(true)
      }
      console.log('📥 [历史列表] 开始加载数据...')
      
      // 使用统一的 /history 接口，后端已实现联合分页
      const response = await apiClient.get(`/user/history?type=${activeTab}&page=${page}&limit=${limit}${filterStatus !== 'all' ? `&status=${filterStatus.toUpperCase()}` : ''}`)
      
      if (!response.success) {
        console.error('❌ [历史列表] 请求失败:', response)
        return
      }

      // 读取分页总数（用于统计和分页）
      const paginationTotalFromApi = response.data?.pagination?.total ?? 0
      setPaginationTotal(paginationTotalFromApi) // 保存当前分页的总数
      
      if (activeTab === 'all' && response.data?.summary) {
        // 全部类型：使用 summary 获取分别的总数
        const summary = response.data.summary
        setVideoTotal(summary.videoTotal ?? 0)
        setImageTotal(summary.imageTotal ?? 0)
        } else {
        // 视频或图片类型：使用 pagination.total
        if (activeTab === 'video') {
          setVideoTotal(paginationTotalFromApi)
          setImageTotal(0)
        } else {
          setVideoTotal(0)
          setImageTotal(paginationTotalFromApi)
        }
      }

      // 页码越界保护：若当前页超过总页数，自动回退到最后一页
      const totalPages = Math.max(1, Math.ceil(paginationTotalFromApi / limit))
      if (paginationTotalFromApi > 0 && page > totalPages) {
        console.warn(`⚠️  [历史列表] 当前页(${page})超过总页数(${totalPages})，回退到最后一页`)
        setPage(totalPages)
        return
      }

      const vlist = (response.data?.videoGenerations || []).map((g: any) => {
        if (g.status?.toLowerCase() === 'completed' && !g.outputVideo) {
          console.warn('⚠️  [历史列表] 已完成但无视频URL:', {
            id: g.id,
            status: g.status,
            outputVideo: g.outputVideo
          })
        }
        
        // 🔥 从 metadata 中提取 pid（用于创建角色）
        let pid: string | undefined;
        try {
          // 调试：打印原始数据
          if (g.status?.toLowerCase() === 'completed') {
            console.log(`🔍 [历史列表] 视频 ${g.id} 原始数据:`, {
              hasMetadata: !!g.metadata,
              metadataType: typeof g.metadata,
              metadata: g.metadata
            });
          }
          
          if (g.metadata) {
            const metadata = typeof g.metadata === 'string' ? JSON.parse(g.metadata) : g.metadata;
            pid = metadata?.pid;
            if (pid) {
              console.log(`✅ [历史列表] 视频 ${g.id} 提取到 pid:`, pid);
            } else {
              console.log(`⚠️  [历史列表] 视频 ${g.id} metadata 中没有 pid:`, metadata);
            }
          } else {
            console.log(`⚠️  [历史列表] 视频 ${g.id} 没有 metadata 字段`);
      }
    } catch (e) {
          console.warn(`❌ [历史列表] 解析视频 ${g.id} 的 metadata 失败:`, e);
        }
        
        const status = g.status?.toLowerCase()
        // 🔥 生成中预览状态，默认进度起始为10%
        const defaultProgress = status === 'processing' ? 10 : undefined
        return {
          id: g.id,
          type: 'video',
          prompt: g.prompt,
          status: status,
          thumbnail: null,
          result: g.outputVideo,
          createdAt: g.createdAt,
          model: g.model,
          duration: g.duration,
          cost: g.cost,
          // 预留进度字段，后续可由同步接口 / SSE 更新
          // 如果后端没有提供进度且状态为生成中，默认10%
          progress: typeof (g as any).progress === 'number' ? (g as any).progress : defaultProgress,
          pid // 🔥 保存 pid，用于创建角色
        }
      })
        
      const ilist = (response.data?.imageGenerations || []).map((g: any) => {
        let outputImages = []
        try {
          outputImages = typeof g.outputImages === 'string' ? JSON.parse(g.outputImages) : (Array.isArray(g.outputImages) ? g.outputImages : [])
        } catch {}
        const statusLower = g.status?.toLowerCase()
        const completedCount = Array.isArray(outputImages) ? outputImages.length : 0
        const displayCount = statusLower === 'completed' ? completedCount : (g.count || 0)
        return {
          id: g.id,
          type: 'image',
          prompt: g.prompt,
          status: statusLower,
          thumbnail: outputImages[0] || undefined,
          result: outputImages,
          createdAt: g.createdAt,
          model: g.model,
          count: displayCount,
          cost: g.cost,
        }
      })
      
      const allData = [...vlist, ...ilist].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      // 计算是否存在进行中的任务（非 completed/failed/canceled）
      const nextHasInProgress = allData.some((item) => {
        const s = (item.status || '').toLowerCase()
        return s && !['completed', 'failed', 'canceled'].includes(s)
      })
      setHasInProgress(nextHasInProgress)

      // 仅在数据实际变化时才更新，避免无意义的重渲染
      setData((prev) => {
        if (prev.length === allData.length) {
          let isSame = true
          for (let i = 0; i < prev.length; i++) {
            const a: any = prev[i]
            const b: any = allData[i]
            if (
              a.id !== b.id ||
              a.type !== b.type ||
              a.status !== b.status ||
              a.result !== b.result ||
              a.progress !== b.progress ||
              a.createdAt !== b.createdAt
            ) {
              isSame = false
              break
            }
          }
          if (isSame) {
            console.log('🔍 [历史列表] 轮询数据与当前状态一致，跳过状态更新')
            return prev
          }
        }

        console.log(`✅ [历史列表] 数据加载完成: ${vlist.length}个视频, ${ilist.length}个图片`)
        return allData
      })
      // 清理失败状态，让新加载的数据可以重新尝试加载
      setFailedMediaIds(new Set())
    } catch (e: any) {
      console.error('❌ [历史列表] 数据加载失败:', e.message || e)
      if (e?.response?.status === 403) {
        message.warning('登录已过期，请重新登录')
      }
    } finally {
      // 延迟设置加载状态为 false，确保数据已渲染
      setTimeout(() => {
      setIsLoading(false)
      }, 100)
    }
  }

  // 依赖分页/筛选条件的首次加载
  useEffect(() => {
    loadData()
  }, [page, limit, filterStatus, activeTab])

  // 仅在存在进行中的任务时开启轮询；页面不可见时暂缓请求
  useEffect(() => {
    if (!hasInProgress) {
      return
    }

    // 每30秒自动刷新一次数据（仅在有进行中任务时）
    const timer = setInterval(() => {
      if (document.hidden) {
        // 标签页不可见时不发起请求，减少无效轮询
        return
      }
      console.log('🔄 [历史列表] 定时刷新数据（存在进行中任务）...')
    loadData()
    }, 30000)

    return () => clearInterval(timer)
  }, [hasInProgress, page, limit, filterStatus, activeTab])

  // 监听窗口大小变化，更新移动端状态
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // 切换类型或状态时重置到第1页
  useEffect(() => {
    setPage(1)
  }, [activeTab, filterStatus])

  // 实时合并：当有任务完成或进行中状态变化 - 优化更新逻辑，减少抖动
  useEffect(() => {
        const merge = (p: any) => {
      console.log('🔔 [历史列表] 收到事件更新:', p)
      
        setData(prev => {
        const idx = prev.findIndex((i: any) => i.id === p.id)
        if (idx >= 0) {
          // 更新现有记录 - 使用浅拷贝避免整个数组重新创建
          const currentItem = prev[idx]
          
          // 🔥 修复：如果当前状态是失败，且新状态是processing，且没有outputVideo，保持失败状态
          // 防止失败任务被错误地更新为处理中状态
          if (currentItem.status === 'failed' && 
              p.status?.toLowerCase() === 'processing' && 
              !p.outputVideo && 
              !currentItem.result) {
            console.log(`⚠️  [历史列表] 任务已失败，收到processing事件但无结果，保持失败状态，不更新`);
            return prev;
          }
          
          // 🔥 修复：如果当前状态是失败且已退款，不应该再更新为其他状态（包括 PENDING）
          // 防止失败后自动重新生成
          if (currentItem.status === 'failed' && 
              (p.status?.toLowerCase() === 'pending' || p.status?.toLowerCase() === 'processing') &&
              !p.outputVideo) {
            console.log(`⚠️  [历史列表] 任务已失败，收到 ${p.status} 事件但无结果，保持失败状态，不更新`);
            return prev;
          }
          
          const newStatus = p.status?.toLowerCase()
          const newResult = p.outputVideo || p.outputImages || currentItem.result
          // 🔥 优先使用事件中的进度值，如果事件中没有则保留现有进度值
          // 如果状态是 processing，确保进度值被正确更新
          // 🔥 生成中预览状态，默认进度起始为10%
          const defaultProgress = newStatus === 'processing' ? 10 : undefined
          
          // 🔥 防止进度回滚：如果新进度小于当前进度，保留当前进度
          let newProgress: number | undefined
          if (typeof p.progress === 'number' && !isNaN(p.progress) && p.progress >= 0) {
            // 事件中有新进度值
            if (typeof currentItem.progress === 'number' && !isNaN(currentItem.progress) && currentItem.progress >= 0) {
              // 当前已有进度值，取较大值防止回滚
              newProgress = Math.max(p.progress, currentItem.progress, 10)
            } else {
              // 当前没有进度值，使用新进度值（至少10%）
              newProgress = Math.max(p.progress, 10)
            }
          } else {
            // 事件中没有进度值，保留当前进度或使用默认值
            newProgress = currentItem.progress !== undefined ? currentItem.progress : defaultProgress
          }
          
          // 🔥 特殊处理：如果进度值从 undefined 变为有效数字，或者进度值有变化，需要更新
          const progressChanged = (
            (currentItem.progress === undefined && typeof newProgress === 'number') ||
            (typeof currentItem.progress === 'number' && typeof newProgress === 'number' && currentItem.progress !== newProgress) ||
            (typeof p.progress === 'number' && p.progress !== currentItem.progress) // 🔥 如果事件中有进度值且与当前不同，需要更新
          )
          
          console.log('📊 [历史列表] 进度更新:', {
            id: p.id,
            eventProgress: p.progress,
            currentProgress: currentItem.progress,
            newProgress,
            hasProgress: typeof newProgress === 'number',
            progressChanged,
            willUpdate: progressChanged || currentItem.status !== newStatus || currentItem.result !== newResult,
            progressRollbackPrevented: typeof p.progress === 'number' && typeof currentItem.progress === 'number' && p.progress < currentItem.progress
          })
          
          // 🔥 如果状态、结果、进度和 PID 都没有变化，不更新，避免不必要的重新渲染
          // 注意：需要比较 PID，因为创建角色成功后 PID 会更新
          const newPid = p.pid || currentItem.pid
          
          // 🔥 检测 PID 是否有变化
          const pidChanged = (
            (currentItem.pid === undefined && newPid !== undefined) ||
            (currentItem.pid !== undefined && newPid !== undefined && currentItem.pid !== newPid) ||
            (p.pid && p.pid !== currentItem.pid) // 🔥 如果事件中有 PID 且与当前不同，需要更新
          )
          
          if (
            currentItem.status === newStatus &&
            currentItem.result === newResult &&
            !progressChanged && // 🔥 如果进度值有变化，需要更新
            !pidChanged && // 🔥 如果 PID 有变化，需要更新
            currentItem.progress === newProgress &&
            currentItem.pid === newPid
          ) {
            console.log('⏭️  [历史列表] 数据无变化，跳过更新')
                return prev
            }
          
          const updated = {
            ...currentItem,
            status: newStatus,
            result: newResult,
            progress: newProgress, // 🔥 确保进度值被正确设置
            // 🔥 如果 SSE 事件包含 pid，更新本地 pid（防止丢失）
            // 优先使用事件中的 pid，如果事件中没有则保留现有的 pid
            pid: p.pid || currentItem.pid || newPid
          }
          
          console.log('✅ [历史列表] 更新记录:', {
            id: p.id,
            oldStatus: currentItem.status,
            newStatus: updated.status,
            oldResult: currentItem.result,
            newResult: updated.result,
            oldProgress: currentItem.progress,
            newProgress: updated.progress,
            newProgressType: typeof updated.progress,
            isProgressValid: typeof updated.progress === 'number' && !isNaN(updated.progress) && updated.progress >= 0,
            oldPid: currentItem.pid,
            eventPid: p.pid,
            newPid: updated.pid,
            pidUpdated: p.pid && p.pid !== currentItem.pid
          })
          
          // 创建新数组，但只更新变化的项
            const next = [...prev]
          next[idx] = updated

          // 合并更新后重新计算是否还有进行中的任务
          const stillHasInProgress = next.some((item: any) => {
            const s = (item.status || '').toLowerCase()
            return s && !['completed', 'failed', 'canceled'].includes(s)
          })
          if (stillHasInProgress !== hasInProgress) {
            setHasInProgress(stillHasInProgress)
          }
            return next
        }
        
        // 新增记录 - 只在当前页面是第1页时添加到列表顶部
        if (page === 1) {
          const status = p.status?.toLowerCase()
          // 🔥 生成中预览状态，默认进度起始为10%
          const defaultProgress = status === 'processing' ? 10 : undefined
          const entry = { 
            id: p.id, 
            type: p.type || (p.outputImages ? 'image' : 'video'), 
            status: status, 
            result: p.outputVideo || p.outputImages, 
            progress: typeof p.progress === 'number' ? p.progress : defaultProgress,
            createdAt: new Date().toISOString(), 
            prompt: p.prompt || '' 
          }
          
          console.log('➕ [历史列表] 新增记录:', entry)
          return [entry, ...prev]
        }
        
        // 不在第1页时，不添加新记录，避免抖动
        return prev
      })
    }
    onEvent('video_updated', merge)
    onEvent('image_updated', merge)
    
    // 🔥 监听角色创建事件，自动关闭 Modal 并提示
    const characterHandler = (payload: any) => {
      console.log('🔔 [历史列表] 收到角色创建事件:', payload)
      
      if (payload.status === 'COMPLETED' && payload.character_id) {
        // 角色创建成功，关闭 loading 提示，显示成功消息
        message.destroy('character_creating') // 关闭 loading 提示
        message.success({
          content: `角色 "${payload.name}" 创建成功！`,
          duration: 3
        })
        // 确保 Modal 已关闭
        setCreateCharacterModal({
          visible: false,
          videoItem: null,
          characterName: '',
          timestamps: '0,3',
          loading: false
        })
      } else if (payload.status === 'FAILED') {
        // 角色创建失败，关闭 loading 提示，显示错误消息
        message.destroy('character_creating') // 关闭 loading 提示
        message.error({
          content: payload.error || `角色 "${payload.name}" 创建失败`,
          duration: 5
        })
        // 确保 Modal 已关闭
        setCreateCharacterModal({
          visible: false,
          videoItem: null,
          characterName: '',
          timestamps: '0,3',
          loading: false
        })
      }
    }
    onEvent('character_created', characterHandler)
  }, [page])

  // 根据activeTab、filterStatus和搜索关键词过滤数据 - 使用 useMemo 优化性能
  const filteredHistory = useMemo(() => {
    return data.filter(item => {
      const typeMatch = activeTab === 'all' || 
        (activeTab === 'video' && item.type === 'video') ||
        (activeTab === 'image' && item.type === 'image')
      
      const statusMatch = filterStatus === 'all' || item.status === filterStatus

      // 🔍 关键词匹配（支持提示词、模型名、ID等）
      const keyword = searchKeyword.trim().toLowerCase()
      const keywordMatch =
        !keyword ||
        (item.prompt && String(item.prompt).toLowerCase().includes(keyword)) ||
        (item.model && String(item.model).toLowerCase().includes(keyword)) ||
        (item.id && String(item.id).toLowerCase().includes(keyword)) ||
        (item.negativePrompt && String(item.negativePrompt).toLowerCase().includes(keyword)) ||
        (item.status && String(item.status).toLowerCase().includes(keyword))
      
      return typeMatch && statusMatch && keywordMatch
    })
  }, [data, activeTab, filterStatus, searchKeyword])
  
  // 🔍 搜索关键词变化时重置到第一页
  useEffect(() => {
    if (searchKeyword.trim() && page !== 1) {
      setPage(1)
    }
  }, [searchKeyword])
  
  // 调试: 监控数据变化
  useEffect(() => {
    console.log(`📊 [前端] 数据状态更新:`, {
      总数: data.length,
      过滤后: filteredHistory.length,
      当前Tab: activeTab,
      当前筛选: filterStatus,
      搜索关键词: searchKeyword,
      当前页: page
    })
  }, [data, filteredHistory.length, activeTab, filterStatus, searchKeyword, page])


  // 预览视频（使用Modal居中展示）
  const handlePreviewVideo = (item: any) => {
    if (!item.result) {
      message.warning('视频文件不存在')
      return
    }
    
    setVideoPreviewModal({
      visible: true,
      videoUrl: item.result,
      prompt: item.prompt || '未命名作品'
    })
  }
  
  // 关闭视频预览Modal
  const handleCloseVideoPreview = () => {
    setVideoPreviewModal({
      visible: false,
      videoUrl: '',
      prompt: ''
    })
  }
  
  // 打开图片预览Modal
  const handlePreviewImage = (item: any) => {
    if (!item.result || item.result.length === 0) {
      message.warning('图片文件不存在')
      return
    }
    
    setImagePreviewModal({
      visible: true,
      imageUrls: item.result,
      prompt: item.prompt || '未命名作品'
    })
  }
  
  // 关闭图片预览Modal
  const handleCloseImagePreview = () => {
    setImagePreviewModal({
      visible: false,
      imageUrls: [],
      prompt: ''
    })
  }

  // 检测是否为移动设备
  const isMobileDevice = () => {
    return /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  }

  // 检测是否为iOS设备
  const isIOSDevice = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent)
  }

  // 下载文件
  const handleDownload = async (url: string, filename: string, isVideo: boolean = false, skipLoading: boolean = false) => {
    // 防止重复下载：如果该 URL 正在下载中，直接返回
    if (downloadingUrls.has(url)) {
      console.log('⏸️  [下载] 该文件正在下载中，跳过重复请求:', url)
      return
    }

    // 使用唯一的 key 来管理 loading 消息
    const loadingKey = `download_${url}_${Date.now()}`
    
    try {
      // 标记为下载中
      setDownloadingUrls(prev => new Set(prev).add(url))
      
      // 只有在非批量下载模式下才显示 loading
      if (!skipLoading) {
        message.loading({ content: '正在准备下载...', key: loadingKey, duration: 0 })
      }
      
      // 移动端特殊处理
    if (isMobileDevice()) {
        if (isVideo) {
          // 移动端视频：打开新窗口，提示用户长按保存
        window.open(url, '_blank')
          if (!skipLoading) {
            message.destroy(loadingKey)
          }
          if (isIOSDevice()) {
            message.info('视频已打开，请长按视频选择"保存到相册"', 5)
    } else {
            message.info('视频已打开，请点击右上角菜单选择"下载"或"保存"', 5)
          }
        } else {
          // 移动端图片：尝试直接下载，如果失败则打开新窗口
          try {
            const response = await fetch(url)
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
            link.href = blobUrl
        link.download = filename
            link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
            window.URL.revokeObjectURL(blobUrl)
            if (!skipLoading) {
              message.destroy(loadingKey)
              message.success('下载成功，请查看相册或下载文件夹')
            }
          } catch (fetchError) {
            // 如果下载失败，打开新窗口让用户手动保存
            window.open(url, '_blank')
            if (!skipLoading) {
              message.destroy(loadingKey)
              message.info('图片已打开，请长按图片选择"保存到相册"', 5)
            }
          }
        }
        // 清除下载状态
        setDownloadingUrls(prev => {
          const next = new Set(prev)
          next.delete(url)
          return next
        })
        return
      }
      
      // PC端：使用 <a download> 直接触发浏览器下载，避免打开新页面
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      if (!skipLoading) {
        message.destroy(loadingKey)
        message.success('下载已开始，请在浏览器下载列表中查看')
      }
    } catch (error) {
      console.error('下载失败:', error)
      if (!skipLoading) {
        message.destroy(loadingKey)
        message.error('下载失败，请重试')
      }
    } finally {
      // 清除下载状态
      setDownloadingUrls(prev => {
        const next = new Set(prev)
        next.delete(url)
        return next
      })
    }
  }

  // 下载视频
  const handleDownloadVideo = (item: any) => {
    if (!item.result) {
      message.warning('视频文件不存在')
      return
    }
    
    const filename = `${item.prompt || '未命名作品'}_${item.id}.mp4`.replace(/[^\w\s-]/g, '')
    handleDownload(item.result, filename, true) // 传递 isVideo=true
  }

  // 下载图片
  const handleDownloadImage = (item: any) => {
    if (!item.result || item.result.length === 0) {
      message.warning('图片文件不存在')
      return
    }
    
    if (item.result.length === 1) {
      // 单张图片
      const filename = `${item.prompt || '未命名作品'}_${item.id}.png`.replace(/[^\w\s-]/g, '')
      handleDownload(item.result[0], filename)
    } else {
      // 多张图片，逐个下载
      // 使用统一的 loading key，避免多个 loading 提示
      const batchLoadingKey = `batch_download_${item.id}_${Date.now()}`
      message.loading({ 
        content: `正在准备下载 ${item.result.length} 张图片...`, 
        key: batchLoadingKey, 
        duration: 0 
      })
      
      let completedCount = 0
      const totalCount = item.result.length
      
      item.result.forEach((url: string, index: number) => {
        const filename = `${item.prompt || '未命名作品'}_${item.id}_${index + 1}.png`.replace(/[^\w\s-]/g, '')
        setTimeout(async () => {
          try {
            // 批量下载时跳过单个文件的 loading 提示
            await handleDownload(url, filename, false, true)
            completedCount++
            
            // 所有图片下载完成后，关闭统一的 loading
            if (completedCount === totalCount) {
              message.destroy(batchLoadingKey)
              message.success(`已开始下载 ${totalCount} 张图片`)
            }
          } catch (error) {
            completedCount++
            // 即使有失败，也要更新计数
            if (completedCount === totalCount) {
              message.destroy(batchLoadingKey)
            }
          }
        }, index * 500) // 延迟500ms避免浏览器阻止
      })
    }
  }

  // 收藏/取消收藏
  const handleFavorite = async (item: any) => {
    try {
      // 这里可以调用后端API来实现收藏功能
      // 暂时用本地存储模拟
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      const existingIndex = favorites.findIndex((fav: any) => fav.id === item.id)
      
      if (existingIndex >= 0) {
        // 取消收藏
        favorites.splice(existingIndex, 1)
        message.success('已取消收藏')
      } else {
        // 添加收藏
        favorites.push({
          id: item.id,
          type: item.type,
          prompt: item.prompt,
          result: item.result,
          createdAt: item.createdAt,
          favoritedAt: new Date().toISOString()
        })
        message.success('已添加到收藏')
      }
      
      localStorage.setItem('favorites', JSON.stringify(favorites))
    } catch (error) {
      console.error('收藏操作失败:', error)
      message.error('操作失败，请重试')
    }
  }

  // 单个删除
  const handleDeleteItem = async (item: any) => {
    try {
      const endpoint = item.type === 'video' ? `/video/generations/${item.id}` : `/image/generations/${item.id}`
      await apiClient.delete(endpoint)
      
      message.success('删除成功')
      
      // 更新本地数据，不刷新整个列表
      setData(prev => prev.filter(d => d.id !== item.id))

      // 更新分页总数
      setPaginationTotal(prev => Math.max(0, prev - 1))
      
      // 更新统计总数
      if (item.type === 'video') {
        setVideoTotal(prev => Math.max(0, prev - 1))
      } else if (item.type === 'image') {
        setImageTotal(prev => Math.max(0, prev - 1))
      }
    } catch (error: any) {
      console.error('删除失败:', error)
      message.error('删除失败: ' + (error?.message || '未知错误'))
    }
  }

  // 检查是否已收藏
  const isFavorited = (item: any) => {
    try {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      return favorites.some((fav: any) => fav.id === item.id)
    } catch {
      return false
    }
  }

  // 🔥 打开创建角色Modal
  const handleOpenCreateCharacterModal = (item: any) => {
    // 🔥 即使没有 PID，也可以通过视频 URL 创建角色
    if (!item.pid && !item.result) {
      message.warning('该视频没有 PID 和视频 URL，无法创建角色')
      return
    }
    
    setCreateCharacterModal({
      visible: true,
      videoItem: item,
      characterName: '',
      timestamps: '0,3',
      loading: false
    })
  }
  
  // 🔥 从原视频创建角色（支持 PID 和视频 URL 两种方式）
  const handleCreateCharacter = async () => {
    const { videoItem, characterName, timestamps } = createCharacterModal
    
    console.log('🔍 [创建角色] 开始创建角色，参数:', {
      videoId: videoItem?.id,
      pid: videoItem?.pid,
      videoUrl: videoItem?.result,
      characterName,
      timestamps
    });
    
    if (!characterName || !characterName.trim()) {
      message.error('请输入角色名称')
      return
    }
    
    // 验证 timestamps 格式
    const timestampPattern = /^\d+,\d+$/;
    if (!timestampPattern.test(timestamps)) {
      message.error('时间戳格式错误，应为 "开始秒数,结束秒数"，例如: "0,3"')
      return
    }
    
    // 🔥 判断使用哪种方式创建角色
    const hasPid = !!videoItem?.pid;
    const hasVideoUrl = !!videoItem?.result;
    
    if (!hasPid && !hasVideoUrl) {
      message.error('该视频没有 PID 和视频 URL，无法创建角色')
      return
    }
    
    console.log('✅ [创建角色] 参数验证通过，发送API请求');
    console.log(`📋 [创建角色] 使用方式: ${hasPid ? 'PID' : '视频 URL'}`);
    setCreateCharacterModal(prev => ({ ...prev, loading: true }))
    
    try {
      let response;
      
      if (hasPid) {
        // 🔥 有 PID，使用 /create-character 接口（推荐方式）
        const requestPayload = {
          pid: videoItem.pid,
          timestamps,
          name: characterName.trim()
        };
        console.log('📤 [创建角色] API 请求参数 (PID方式):', requestPayload);
        response = await apiClient.post('/video/create-character', requestPayload)
      } else {
        // 🔥 没有 PID 但有视频 URL，使用 /upload-character 接口
        const requestPayload = {
          url: videoItem.result,
          timestamps,
          name: characterName.trim()
        };
        console.log('📤 [创建角色] API 请求参数 (URL方式):', requestPayload);
        response = await apiClient.post('/video/upload-character', requestPayload)
      }
      
      console.log('📥 [创建角色] API 响应:', response);
      
      if (response.success) {
        if (response.data?.status === 'PROCESSING') {
          // 🔥 异步处理中，立即关闭 Modal 并显示提示（30秒后自动关闭）
          message.loading({
            content: `角色 "${characterName}" 创建中，处理完成后会自动通知...`,
            duration: 30, // 30秒后自动关闭
            key: 'character_creating'
          })
          // 立即关闭 Modal，等待 SSE 事件通知
          setCreateCharacterModal({
            visible: false,
            videoItem: null,
            characterName: '',
            timestamps: '0,3',
            loading: false
          })
        } else {
          // 同步返回（兼容旧版本）
          message.success('角色创建成功！')
          console.log('✅ [创建角色] 角色创建成功，关闭Modal');
          setCreateCharacterModal({
            visible: false,
            videoItem: null,
            characterName: '',
            timestamps: '0,3',
            loading: false
          })
        }
      } else {
        throw new Error(response.message || '创建角色失败')
      }
    } catch (error: any) {
      console.error('❌ [创建角色] 创建失败:', error);
      console.error('❌ [创建角色] 错误详情:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      message.error(error?.response?.data?.message || error?.message || '创建角色失败')
    } finally {
      setCreateCharacterModal(prev => ({ ...prev, loading: false }))
    }
  }

  // 同步单个任务状态
  const handleSyncStatus = async (id: string, type: string) => {
    setSyncingIds(prev => new Set(prev).add(id))
    
    try {
      const endpoint = type === 'video' ? `/video/generations/${id}/sync` : `/image/generations/${id}/sync`
      console.log(`🔄 [前端] 开始同步任务:`, { id, type, endpoint })
      
      const response = await apiClient.post(endpoint)
      
      console.log(`📥 [前端] 同步响应 (完整):`, response)
      
      // 正确解析响应格式
      // 后端返回: { success: true, message: "...", data: { status: "...", ... } }
      const apiResponse = response
      const isSuccess = apiResponse.success !== false
      const msg = apiResponse.message || '同步成功'
      const responseData = apiResponse.data || {}
      
      console.log(`📊 [前端] 解析结果:`, { 
        isSuccess, 
        msg, 
        responseData 
      })
      
      if (isSuccess) {
        // 显示提示消息
        if (responseData.status === 'FAILED') {
          // 如果是无法恢复的失败任务，显示更明确的提示
          if (responseData.cannotRecover) {
            message.error({
              content: msg,
              duration: 6
            })
          } else {
            message.warning(msg)
          }
        } else if (responseData.status === 'COMPLETED') {
          message.success(msg)
        } else {
          message.info(msg)
        }
        
        // 🔥 修复：如果同步返回失败状态，确保不再更新为其他状态
        // 如果任务已失败并退款（alreadyRefunded=true），阻止后续状态更新
        if (responseData.status === 'FAILED' && responseData.alreadyRefunded) {
          console.log(`⚠️  [前端] 任务已失败并退款，阻止后续状态更新`);
        }
        
        // 更新本地数据 - 使用函数式更新确保获取最新状态
        console.log(`📋 [前端] 开始更新数据，查找 ID: ${id}`)
        
        setData(prevData => {
          console.log(`📋 [前端] setData 回调执行，当前 data 总数: ${prevData.length}`)
          
          // 🔥 优化：先检查是否有变化，避免不必要的数组创建和重新渲染
          const targetIndex = prevData.findIndex(item => item.id === id)
          if (targetIndex === -1) {
            console.warn(`⚠️  [前端] 未找到 ID 为 ${id} 的项，跳过更新`)
            return prevData // 未找到，直接返回原数组，避免重新渲染
          }
          
          const currentItem = prevData[targetIndex]
          
          // 🔥 修复：如果当前状态是失败且已退款，且同步返回的状态不是失败，保持失败状态
          if (currentItem.status === 'failed' && responseData.alreadyRefunded && responseData.status !== 'FAILED') {
            console.log(`⚠️  [前端] 任务已失败并退款，保持失败状态，不更新为: ${responseData.status}`);
            return prevData // 无变化，返回原数组，避免重新渲染
          }
          
          const newStatus = (responseData.status || 'failed').toLowerCase()
          const newResult = responseData.outputVideo || responseData.outputImages || currentItem.result
          // 🔥 生成中预览状态，默认进度起始为10%
          const defaultProgress = newStatus === 'processing' ? 10 : undefined
          
          // 🔥 防止进度回滚：如果新进度小于当前进度，保留当前进度
          let newProgress: number | undefined
          if (typeof responseData.progress === 'number' && !isNaN(responseData.progress) && responseData.progress >= 0) {
            // 轮询响应中有新进度值
            if (typeof currentItem.progress === 'number' && !isNaN(currentItem.progress) && currentItem.progress >= 0) {
              // 当前已有进度值，取较大值防止回滚
              newProgress = Math.max(responseData.progress, currentItem.progress, 10)
            } else {
              // 当前没有进度值，使用新进度值（至少10%）
              newProgress = Math.max(responseData.progress, 10)
            }
          } else {
            // 轮询响应中没有进度值，保留当前进度或使用默认值
            newProgress = currentItem.progress !== undefined ? currentItem.progress : defaultProgress
          }
          const newPid = responseData.pid || currentItem.pid || undefined
          
          // 🔥 优化：检查是否有实际变化，如果没有变化则返回原数组，避免重新渲染
          const hasChanges = (
            currentItem.status !== newStatus ||
            currentItem.result !== newResult ||
            currentItem.progress !== newProgress ||
            currentItem.pid !== newPid
          )
          
          if (!hasChanges) {
            console.log(`⏭️  [前端] 数据无变化，跳过更新，避免重新渲染`)
            return prevData // 无变化，返回原数组，避免重新渲染
          }
          
          console.log(`✅ [前端] 找到匹配项 (索引 ${targetIndex}):`, { 
            itemId: currentItem.id,
            oldStatus: currentItem.status, 
            newStatus,
            oldResult: currentItem.result,
            newResult,
            hasResult: !!newResult,
            creditsRefunded: responseData.creditsRefunded,
            oldProgress: currentItem.progress,
            newProgress,
            progressRollbackPrevented: typeof responseData.progress === 'number' && typeof currentItem.progress === 'number' && responseData.progress < currentItem.progress
          })
          
          const updated = {
            ...currentItem,
            status: newStatus,
            result: newResult,
            progress: newProgress,
            pid: newPid
          }
          
          // 🔥 如果同步后获取到了 pid，输出日志
          if (responseData.pid && !currentItem.pid) {
            console.log(`✅ [前端] 同步获取到 pid: ${responseData.pid}，已更新到本地数据`);
          }
          
          console.log(`🔄 [前端] 更新后的数据:`, updated)
          
          // 🔥 优化：只创建新数组并更新目标项，其他项保持引用不变
          // 这样 React 可以识别哪些项需要重新渲染，哪些不需要
          const updatedData = [...prevData]
          updatedData[targetIndex] = updated
          
          console.log(`🔄 [前端] 返回新数据数组，总数: ${updatedData.length}`)
          return updatedData
        })
        
        console.log(`✅ [前端] setData 已调用`)
        
        // 🔥 修复：移除 refreshKey 更新，避免整个列表重新渲染导致闪动
        // setData 已经更新了数据，React 会自动重新渲染，无需强制刷新
        // 只有被更新的项会重新渲染，其他项保持不变
        
        // 如果有积分退款，显示通知并刷新用户信息和积分活动记录
        if (responseData.creditsRefunded && responseData.creditsRefunded > 0) {
          console.log(`💰 [前端] 积分已退还: ${responseData.creditsRefunded}，刷新用户积分（无需刷新页面）`)
          
          // 🔥 显示积分退还通知
          message.success({
            content: `积分已退还：${responseData.creditsRefunded} 积分`,
            duration: 4,
            icon: '💰'
          })
          
          try {
            await useAuthStore.getState().refreshUser()
            console.log(`✅ [前端] 用户积分已刷新`)
            
            // 🔥 刷新积分活动记录，确保显示退还记录
            try {
              const { useCreditsActivityStore } = await import('@/store/creditsActivityStore')
              await useCreditsActivityStore.getState().loadFromAPI()
              console.log(`✅ [前端] 积分活动记录已刷新`)
            } catch (activityError) {
              console.warn('⚠️ [前端] 刷新积分活动记录失败:', activityError)
            }
          } catch (refreshError) {
            console.warn('⚠️ [前端] 刷新用户积分失败:', refreshError)
          }
        }
        // 🔥 移除重复提示：后端返回的消息已经包含"积分之前已退还"信息，无需再次显示
        // else if (responseData.alreadyRefunded) {
        //   console.log(`💰 [前端] 积分之前已退还，无需重复退还`)
        //   message.info({
        //     content: '积分之前已退还',
        //     duration: 3
        //   })
        // }
        
        // 🔥 修复：不再重新加载历史列表，避免覆盖已保存的 pid
        // 同步响应已经更新了本地数据（包含 pid），SSE 事件也会包含 pid
        // 重新加载会导致 pid 短暂消失，因为 API 响应可能有延迟
        if (responseData.status === 'COMPLETED' && responseData.pid && type === 'video') {
          console.log(`📹 [前端] 同步获取到 pid: ${responseData.pid}，已更新到本地数据，无需重新加载`)
        }
        
        // 其他状态（COMPLETED / FAILED / alreadyRefunded）都依靠本地状态和 SSE 更新
        console.log(`✅ [前端] 状态已更新为: ${responseData.status}，保持当前页面不刷新`)
      }
    } catch (error: any) {
      console.error(`❌ [前端] 同步失败:`, error)
      console.error(`❌ [前端] 错误响应:`, error?.response?.data)
      
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误'
      message.error({
        content: '同步失败: ' + errorMsg,
        duration: 5
      })
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  // 渲染视频项 - 使用固定结构避免抖动
  const renderVideoItem = (item: any) => (
    <div>
      <Card 
        className="unified-card history-item-card"
        hoverable={false}
      >
        <Row gutter={[0, 0]} className="history-item-row md:!mx-[-10px]">
          {/* 移动端占满整行，避免缩略图过小；桌面保持原比例 */}
          <Col xs={24} sm={8} md={6} className="history-thumb-col md:!px-2.5">
            <div 
              className="relative group history-thumb-wrapper"
            onClick={() => {
                // 如果视频已完成，点击缩略图即可预览
                if (item.status === 'completed' && item.result) {
                  handlePreviewVideo(item)
                }
              }}
              style={{ 
                cursor: item.status === 'completed' && item.result ? 'pointer' : 'default',
                width: '100%',
                height: '144px',
                minHeight: '144px',
                aspectRatio: '16/9',
                overflow: 'hidden'
              }}
            >
              {item.result && !failedMediaIds.has(item.id) ? (
                <video
                  className="w-full h-full rounded-lg shadow-sm"
                  style={{ 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    width: '100%',
                    height: '100%',
                    display: 'block'
                  }}
                  src={item.result}
                  preload="metadata"
                  muted
                  playsInline
                  crossOrigin="anonymous"
                  onLoadedMetadata={(e) => {
                    console.log(`✅ [视频缩略图] 视频元数据加载成功: ${item.id}`, item.result)
                    // 尝试加载第一帧作为缩略图
                    try {
                      const video = e.currentTarget
                      video.currentTime = 0.1 // 设置到0.1秒，避免第一帧可能为黑屏
                    } catch (err) {
                      console.warn(`⚠️ [视频缩略图] 设置currentTime失败: ${item.id}`, err)
                    }
                  }}
                  onMouseEnter={(e) => {
                    const video = e.currentTarget
                    video.play().catch(() => {})
                  }}
                  onMouseLeave={(e) => {
                    const video = e.currentTarget
                    video.pause()
                    video.currentTime = 0
                  }}
                  onError={(e) => {
                    const video = e.currentTarget as HTMLVideoElement
                    console.error(`❌ [视频缩略图] 视频加载失败: ${item.id}`, {
                      src: item.result,
                      error: e,
                      videoError: video.error
                    })
                    // 阻止重复请求：清空 src 并标记为失败
                    video.src = ''
                    video.load() // 清空加载队列
                    // 使用 React 状态管理，而不是直接操作 DOM
                    setFailedMediaIds(prev => new Set(prev).add(item.id))
                  }}
                />
              ) : (
                <div 
                  className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center shadow-sm history-thumb-fallback" 
                  style={{ 
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                    minHeight: '144px'
                  }}
                >
                  {failedMediaIds.has(item.id) ? (
                    <svg className="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 002 0V7zm-1 8a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" clipRule="evenodd" />
                    </svg>
                  ) : (
                  <VideoCameraOutlined style={{ fontSize: 'var(--font-h2-mobile)' }} className="text-blue-400" />
                    )}
                </div>
            )}
              {item.status === 'processing' && (
                <motion.div
                  key={`generating-${item.id}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center overflow-hidden"
                  style={{ 
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                    minHeight: '144px'
                  }}
                >
                  {/* 🎬 生成中预览卡片 - 参考视频生成页面美化版 */}
                  <div 
                    className="relative w-full h-full overflow-hidden" 
                    style={{ 
                      borderRadius: '12px',
                      width: '100%',
                      height: '100%',
                      minHeight: '144px'
                    }}
                  >
                    {/* 四角圆弧边框 */}
                    <div 
                      className="absolute inset-0 border-2 border-white/30" 
                      style={{ 
                        borderRadius: '12px', 
                        pointerEvents: 'none',
                        width: '100%',
                        height: '100%'
                      }}
                    ></div>
                    
                    {/* 动态渐变背景 */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 opacity-80 animate-pulse" 
                      style={{ 
                        borderRadius: '12px',
                        width: '100%',
                        height: '100%'
                      }}
                    ></div>
                    <div 
                      className="absolute inset-0 bg-gradient-to-tr from-yellow-300/30 via-transparent to-blue-300/30 animate-pulse" 
                      style={{ 
                        animationDelay: '0.5s', 
                        borderRadius: '12px',
                        width: '100%',
                        height: '100%'
                      }}
                    ></div>
                    
                    {/* 星光粒子效果 */}
                    <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: '12px' }}>
                      <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-ping opacity-75"></div>
                      <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-yellow-200 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
                      <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-pink-200 rounded-full animate-ping opacity-70" style={{ animationDelay: '0.6s' }}></div>
                    </div>
                    
                    {/* 内容区 */}
                    <div 
                      className="relative backdrop-blur-sm bg-white/20 flex flex-col items-center justify-center text-center"
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '144px',
                        padding: '12px',
                        borderRadius: '12px'
                      }}
                    >
                      {/* 闪烁星符号 */}
                      <div className="relative mb-2 md:mb-3 flex items-center justify-center">
                        <motion.svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-10 h-10 md:w-12 md:h-12 text-yellow-300 drop-shadow-md"
                          animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                        >
                          <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" />
                        </motion.svg>
                      </div>

                      {/* 进度数字 */}
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        key={typeof item.progress === 'number' && !isNaN(item.progress) && item.progress >= 0 ? Math.round(item.progress) : 'default'}
                        className="mb-1.5 md:mb-2"
                      >
                        <Text className="text-white text-lg md:text-xl font-black" style={{ 
                          textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)',
                          letterSpacing: '0.1em'
                        }}>
                          {typeof item.progress === 'number' && !isNaN(item.progress) && item.progress >= 0 
                            ? `${Math.round(item.progress)}%` 
                            : '10%'}
                        </Text>
                      </motion.div>
                      
                      {/* 正在创作中 */}
                      <div className="text-[10px] md:text-xs text-white/95 font-medium drop-shadow">
                        正在创作中
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              {item.status === 'completed' && item.result && (
                <>
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
                </>
              )}
            </div>
          </Col>
          {/* 左右留同样的内边距，保持与缩略图列对齐 */}
          <Col xs={24} sm={16} md={18} className="pl-3 pr-3 md:pl-4 md:pr-4 history-info-col">
            <div className="h-full flex flex-col justify-between">
              <div>
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  className="mb-3 md:mb-1.5 text-gray-800 font-semibold text-responsive-base"
                  style={{ lineHeight: 1.5 }}
                >
                  {item.prompt || '未命名作品'}
            </Paragraph>
                <Space wrap className="md:!mb-1.5" style={{ marginBottom: 'var(--spacing-md)' }} size="small">
                  <Tag color="blue" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <VideoCameraOutlined /> {getProviderName(item.model, 'video')}
                  </Tag>
                  {/* 🔥 显示 pid 状态（仅对已完成的视频且有 PID 时显示） */}
                  {item.status === 'completed' && item.pid && (
                    <Tag 
                      color="green" 
                      style={{ borderRadius: '12px', fontSize: '11px' }}
                      title={`完整 PID: ${item.pid}`}
                    >
                      PID: {item.pid.substring(0, 10)}...
                    </Tag>
                  )}
                </Space>
              </div>
              <div className="flex items-center justify-between mt-3 md:mt-1.5">
                <Space size="small">
                  {/* 只在非完成状态时显示状态标签 */}
                  {item.status !== 'completed' && item.status !== 'failed' && (
                    <Tag 
                      color={item.status === 'processing' ? 'processing' : 'default'}
                      style={{ borderRadius: '12px' }}
                    >
                      {item.status === 'processing'
                        ? '⏳ 处理中'
                          : '⏸ 待处理'}
                    </Tag>
                  )}
                  {/* 🔥 失败状态显示为删除按钮 */}
                  {item.status === 'failed' && (
                        <Button 
                      size="small" 
                      type="text"
                      danger
                      onClick={() => handleDeleteItem(item)}
                      className="flex-shrink-0 history-action-btn violation-btn"
                      style={{ 
                        borderRadius: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none'
                      }}
                    >
                      <span>✗</span>
                      <span> 违规</span>
                    </Button>
                  )}
                </Space>
                <div className="flex items-center gap-1 flex-nowrap">
                  {item.status === 'completed' && (
                    <>
                    <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewVideo(item)}
                        className="border-gray-300 flex-shrink-0 history-action-btn"
                        style={{ 
                          borderRadius: '20px'
                        }}
                      >
                        <span className="hidden sm:inline">预览</span>
                      </Button>
                      {/* 🔥 从原视频创建角色按钮（有 PID 或视频 URL 时显示） */}
                      {(item.pid || item.result) && (
                        <Button 
                          size="small" 
                          icon={<UserAddOutlined />}
                          onClick={() => handleOpenCreateCharacterModal(item)}
                          className="border-blue-300 flex-shrink-0 history-action-btn"
                          style={{ 
                            borderRadius: '20px'
                          }}
                        >
                          <span className="hidden sm:inline">创建角色</span>
                        </Button>
                      )}
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />} 
                        type="primary"
                        onClick={() => handleDownloadVideo(item)}
                        className="flex-shrink-0 history-action-btn"
                        style={{ 
                          borderRadius: '20px'
                        }}
                      >
                        <span className="hidden sm:inline">下载</span>
                      </Button>
                    </>
                  )}
                  {(item.status === 'failed' || item.status === 'pending' || item.status === 'processing') && (
                    <Button 
                      size="small" 
                      icon={<SyncOutlined spin={syncingIds.has(item.id)} />}
                      onClick={() => handleSyncStatus(item.id, 'video')}
                      loading={syncingIds.has(item.id)}
                      className="border-orange-300 flex-shrink-0 history-action-btn"
                      style={{ 
                        borderRadius: '20px'
                      }}
                    >
                      <span className="hidden sm:inline">同步状态</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )

  // 渲染图片项 - 使用固定结构避免抖动
  const renderImageItem = (item: any) => (
    <div>
      <Card 
        className="unified-card history-item-card"
        hoverable={false}
      >
        <Row gutter={[0, 0]} className="history-item-row md:!mx-[-10px]">
          <Col xs={24} sm={8} md={6} className="history-thumb-col md:!px-2.5">
            <div 
              className="relative group history-thumb-wrapper"
              onClick={() => {
                // 点击缩略图预览
                if (item.status === 'completed' && item.result && item.result.length > 0) {
                  handlePreviewImage(item)
                }
              }}
              style={{ 
                cursor: item.status === 'completed' && item.result ? 'pointer' : 'default',
                width: '100%',
                height: '144px',
                minHeight: '144px',
                aspectRatio: '1/1',
                overflow: 'hidden'
              }}
            >
              {!failedMediaIds.has(item.id) && (item.thumbnail || (item.result && item.result.length > 0)) ? (
                <img
                  src={item.thumbnail || item.result[0]}
                  alt="Generated image"
                  loading={item.index !== undefined && item.index < 6 ? "eager" : "lazy"}
                  decoding="async"
                  className="w-full h-full rounded-lg shadow-sm"
                  style={{ 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    width: '100%',
                    height: '100%',
                    display: 'block'
                  }}
                  onError={() => {
                    // 使用 React 状态管理，而不是直接操作 DOM
                    setFailedMediaIds(prev => new Set(prev).add(item.id))
                  }}
                />
              ) : (
                <div 
                  className="w-full h-full bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-lg flex items-center justify-center shadow-sm history-thumb-fallback" 
                  style={{ 
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                    minHeight: '144px'
                  }}
                >
                  {failedMediaIds.has(item.id) ? (
                    <svg className="text-4xl text-purple-400" fill="currentColor" viewBox="0 0 20 20" width="40" height="40">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  ) : (
                  <PictureOutlined style={{ fontSize: 'var(--font-h2-mobile)' }} className="text-purple-400" />
                  )}
                </div>
              )}
              
              {/* 状态遮罩层 */}
              {item.status === 'processing' && (
                <motion.div
                  key={`generating-img-${item.id}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center overflow-hidden bg-slate-900 rounded-xl pointer-events-none"
                >
                  {/* 1. 注入 CSS 动画 */}
                  <style>{`
                    @keyframes aurora-flow {
                      0% { background-position: 0% 50%; }
                      50% { background-position: 100% 50%; }
                      100% { background-position: 0% 50%; }
                    }
                    .animate-aurora-flow {
                      background-size: 200% 200%;
                      animation: aurora-flow 6s ease infinite;
                    }
                  `}</style>

                  {/* 2. 动态背景层 */}
                  {/* 极光渐变 */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-black animate-aurora-flow opacity-90"></div>
                  {/* 噪点纹理 (增加质感) */}
                  <div className="absolute inset-0 opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

                  {/* 3. 核心视觉区 (AI 灵感星) */}
                  <div className="relative z-10 flex flex-col items-center">
                    
                    {/* 图标容器 */}
                    <div className="relative w-24 h-24 mb-4 flex items-center justify-center">
                      {/* 背景光晕 */}
                      <div className="absolute inset-0 bg-purple-500/30 blur-[40px] rounded-full animate-pulse"></div>

                      {/* 伴随小星星 1 (左下) - 闪烁 */}
                      <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="absolute bottom-2 left-1 w-6 h-6 text-blue-300 drop-shadow-md"
                        animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                      >
                        <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" />
                      </motion.svg>

                      {/* 伴随小星星 2 (右上) - 闪烁 */}
                      <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="absolute top-1 right-1 w-5 h-5 text-purple-300 drop-shadow-md"
                        animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                      >
                        <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" />
                      </motion.svg>
                      </div>

                    {/* 进度数字 */}
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      key={typeof item.progress === 'number' && !isNaN(item.progress) && item.progress >= 0 ? Math.round(item.progress) : 'default'}
                      className="mb-3"
                    >
                      <Text className="text-white text-2xl font-black" style={{ 
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)',
                        letterSpacing: '0.1em'
                      }}>
                        {typeof item.progress === 'number' && !isNaN(item.progress) && item.progress >= 0 
                          ? `${Math.round(item.progress)}%` 
                          : '10%'}
                        </Text>
                    </motion.div>
                    
                    {/* 状态主标题 */}
                    <h3 className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-gray-300 mb-3 tracking-[0.2em] uppercase drop-shadow-lg">
                      生成中
                    </h3>
                    
                    {/* 动态声波条 (模拟AI思考) */}
                    <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 flex items-center gap-2 shadow-lg">
                      <div className="flex gap-1 items-end h-3">
                        <motion.div animate={{ height: [4, 12, 4] }} transition={{ duration: 0.8, repeat: Infinity }} className="w-0.5 bg-blue-300 rounded-full shadow-[0_0_6px_rgba(147,197,253,0.8)]" />
                        <motion.div animate={{ height: [6, 16, 6] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.1 }} className="w-0.5 bg-purple-300 rounded-full shadow-[0_0_6px_rgba(216,180,254,0.8)]" />
                        <motion.div animate={{ height: [5, 14, 5] }} transition={{ duration: 0.7, repeat: Infinity, delay: 0.2 }} className="w-0.5 bg-pink-300 rounded-full shadow-[0_0_6px_rgba(249,168,212,0.8)]" />
                        <motion.div animate={{ height: [4, 10, 4] }} transition={{ duration: 0.8, repeat: Infinity, delay: 0.3 }} className="w-0.5 bg-indigo-300 rounded-full shadow-[0_0_6px_rgba(165,180,252,0.8)]" />
                      </div>
                      <span className="text-white/80 text-[9px] font-mono tracking-widest font-bold">AI PROCESSING...</span>
                  </div>
                </div>

                  {/* 4. 底部发光进度条 */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-white/5">
                    <motion.div 
                      initial={{ width: "0%" }}
                      animate={{ width: typeof item.progress === 'number' && !isNaN(item.progress) && item.progress >= 0 ? `${item.progress}%` : "10%" }} 
                      transition={{ duration: 1, ease: "circOut" }}
                      className="h-full bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 shadow-[0_0_20px_rgba(192,132,252,1)]"
                    />
                  </div>
                </motion.div>
              )}
              
              {/* 完成状态标记 */}
              {item.status === 'completed' && item.thumbnail && (
                <>
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg pointer-events-none"></div>
                </>
              )}
            </div>
          </Col>
          <Col xs={24} sm={16} md={18} className="pl-3 md:pl-4 history-info-col">
            <div className="h-full flex flex-col justify-between">
              <div>
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  className="mb-3 md:mb-1.5 text-gray-800 font-semibold text-responsive-base"
                  style={{ lineHeight: 1.5 }}
                >
                  {item.prompt || '未命名作品'}
                </Paragraph>
                <Space wrap className="md:!mb-1.5" style={{ marginBottom: 'var(--spacing-md)' }} size="small">
                  <Tag color="purple" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <PictureOutlined /> {getProviderName(item.model, 'image')}
                  </Tag>
                </Space>
            </div>
              <div className="flex items-center justify-between mt-3 md:mt-1.5">
                <Space size="small">
                  {/* 只在非完成状态时显示状态标签 */}
                  {item.status !== 'completed' && item.status !== 'failed' && (
                    <Tag 
                      color={item.status === 'processing' ? 'processing' : 'default'}
                      style={{ borderRadius: '12px' }}
                    >
                      {item.status === 'processing'
                        ? '⏳ 处理中'
                          : '⏸ 待处理'}
                    </Tag>
                  )}
                  {/* 🔥 失败状态显示为删除按钮 */}
                  {item.status === 'failed' && (
                    <Button 
                      size="small" 
                      type="text"
                      danger
                      onClick={() => handleDeleteItem(item)}
                      className="flex-shrink-0 history-action-btn violation-btn"
                      style={{ 
                        borderRadius: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none'
                      }}
                    >
                      <span>✗</span>
                      <span> 违规</span>
                    </Button>
                  )}
                </Space>
                <div className="flex items-center gap-1 flex-nowrap">
                  {item.status === 'completed' && (
                    <>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewImage(item)}
                        className="border-gray-300 flex-shrink-0 history-action-btn"
                        style={{ borderRadius: '20px' }}
                      >
                        <span className="hidden sm:inline">
                          预览 {item.result && item.result.length > 1 ? `(${item.result.length}张)` : ''}
                        </span>
                      </Button>
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />} 
                        type="primary"
                        onClick={() => handleDownloadImage(item)}
                        className="flex-shrink-0 history-action-btn"
                        style={{ borderRadius: '20px' }}
                      >
                        <span className="hidden sm:inline">下载</span>
                      </Button>
                    </>
                  )}
                  {(item.status === 'failed' || item.status === 'pending' || item.status === 'processing') && (
                    <Button 
                      size="small" 
                      icon={<SyncOutlined spin={syncingIds.has(item.id)} />}
                      onClick={() => handleSyncStatus(item.id, 'image')}
                      loading={syncingIds.has(item.id)}
                      className="border-orange-300 flex-shrink-0 history-action-btn"
                      style={{ 
                        borderRadius: '20px'
                      }}
                    >
                      <span className="hidden sm:inline">同步状态</span>
                    </Button>
                  )}
        </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )

  // 骨架屏组件
  const renderSkeletonItem = () => (
    <Card 
      className="border border-gray-100 unified-card"
      style={{
        marginBottom: 'var(--spacing-lg)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        minHeight: '180px'
      }}
      styles={{ body: { padding: '20px' } }}
    >
      <Row gutter={[0, 0]} className="history-item-row md:!mx-[-10px]">
        <Col xs={24} sm={8} md={6} className="history-thumb-col md:!px-2.5">
          <Skeleton.Image 
            active 
            style={{ 
              width: '100%', 
              height: '144px', 
              minHeight: '144px',
              borderRadius: '12px'
            }} 
          />
        </Col>
        <Col xs={24} sm={16} md={18} className="pl-3 pr-3 md:pl-4 md:pr-4 history-info-col">
          <Skeleton 
            active 
            paragraph={{ rows: 3 }} 
            title={{ width: '80%' }}
          />
        </Col>
      </Row>
    </Card>
  )

  return (
    <div className="history-page min-h-screen w-full relative">
      {/* 禁用历史页内的过渡动画（作用域到 history-page，避免全局影响） */}
      <style>{`
        /* 整个历史页面背景：浅灰，突出白色卡片 */
        .history-page {
          background: #f5f5f7;
        }

        .history-page .ant-list-item,
        .history-page .history-pagination,
        .history-page .history-pagination *,
        .history-page .history-item-row,
        .history-page .history-item-row * {
          transition: none !important;
          animation: none !important;
          transform: none !important;
        }

        /* 顶部筛选、统计等大卡片风格 */
        .history-page .history-card {
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .history-page .history-filter-bar {
          border-radius: 999px;
          background: transparent;
        }

        /* 列表中每一条历史记录卡片：统一为移动端风格（轻量卡片） */
        .history-page .history-item-card {
          border: none !important;
          border-radius: 12px;
          background: #ffffff;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
        }

        /* 列表项之间的垂直间距，由 List.Item 控制 */
        .history-page .history-list-item {
          padding: 0 !important;
          margin-bottom: 12px;
        }

        .history-page .history-list-item:last-child {
          margin-bottom: 0;
        }

        /* 卡片之间的分隔容器：仅作为空白间距使用 */
        .history-page .history-item-divider {
          margin: 8px 0;
          padding: 0;
        }

        .history-page .history-item-divider-line {
          display: none;
        }

        /* 电脑端：底部增加留白，避免列表贴底部 */
        @media (min-width: 768px) {
          .history-page {
            padding-bottom: 32px;
          }
        }

        /* 移动端：统一分页字号，和筛选文字一致 */
        @media (max-width: 767px) {
          .history-page .history-pagination,
          .history-page .history-pagination .ant-pagination-item,
          .history-page .history-pagination .ant-pagination-item a,
          .history-page .history-pagination .ant-pagination-prev,
          .history-page .history-pagination .ant-pagination-next,
          .history-page .history-pagination .ant-pagination-prev button,
          .history-page .history-pagination .ant-pagination-next button {
            font-size: 13px;
          }

          .history-page .history-item-divider {
            margin: 12px 0;
          }
        }
      `}</style>
      {/* 移动端背景装饰 */}
      <div className="md:hidden absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-100/50 to-transparent pointer-events-none page-mobile-hero"></div>
      <div
        className="unified-container relative z-10"
        style={{
          paddingTop: 'var(--spacing-sm)',
        }}
      >
        <div className="flex flex-col">
          {/* 头部设计 - 左右结构 */}
          <div className="w-full page-header-section">
            <div
              className="flex flex-col md:flex-row md:items-center md:justify-between"
              style={{
                marginBottom: 'var(--spacing-lg)',
                gap: 'var(--spacing-md)'
              }}
            >
            {/* 左侧：标题和副标题 */}
            <div className="flex-1">
              <div
                className="flex items-center"
                style={{
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-xs)',
                }}
              >
                <span style={{ fontSize: 'var(--font-h1-mobile)' }}>📚</span>
                <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  我的作品库
                </h1>
               </div>
              <Paragraph
                className="unified-text-secondary"
                style={{
                  fontSize: 'var(--font-body)',
                  marginBottom: 0,
                }}
              >
                查看、管理和下载您的所有AI创作
              </Paragraph>
            </div>
            
            {/* 右侧：特色标签 */}
            <div className="history-header-tags flex flex-wrap gap-2 md:justify-end">
              <Tag icon={<HistoryOutlined />} className="unified-tag bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700">
                {videoTotal + imageTotal} 作品
              </Tag>
              <Tag icon={<VideoCameraOutlined />} className="unified-tag bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700">
                {videoTotal} 视频
              </Tag>
              <Tag icon={<PictureOutlined />} className="unified-tag bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700">
                {imageTotal} 图片
              </Tag>
            </div>
          </div>
          </div>


          {/* 筛选器 */}
          <Card 
            className="unified-card border border-gray-100 history-card" 
            style={{
              marginBottom: 'var(--spacing-md)',
            }}
            styles={{
              body: {
                padding: 'var(--spacing-sm) var(--spacing-md)',
              }
            }}
          >
            {/* 移动端顶部装饰条，参考视频/图像生成页主卡片风格 */}
            <div className="md:hidden bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1.5 w-full rounded-full mb-3"></div>
            <div className="history-filter-bar flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              {/* 移动端：单行筛选按钮 */}
              {isMobile ? (
                <div 
                  className="flex items-center gap-2 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden" 
                  style={{ 
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}
                >
                  <span className="text-sm text-gray-700 font-semibold flex-shrink-0">筛选：</span>
                  <div className="flex items-center gap-2" style={{ minWidth: '100%' }}>
                    <Button
                      type={activeTab === 'all' && filterStatus === 'all' ? 'primary' : 'default'}
                      onClick={() => {
                        setActiveTab('all')
                        setFilterStatus('all')
                      }}
                      size="small"
                      className={activeTab === 'all' && filterStatus === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      全部
                    </Button>
                    <Button
                      type={activeTab === 'video' && filterStatus === 'all' ? 'primary' : 'default'}
                      onClick={() => {
                        setActiveTab('video')
                        setFilterStatus('all')
                      }}
                      size="small"
                      className={activeTab === 'video' && filterStatus === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      视频
                    </Button>
                    <Button
                      type={activeTab === 'image' && filterStatus === 'all' ? 'primary' : 'default'}
                      onClick={() => {
                        setActiveTab('image')
                        setFilterStatus('all')
                      }}
                      size="small"
                      className={activeTab === 'image' && filterStatus === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      图片
                    </Button>
                    <Button
                      type={filterStatus === 'failed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('failed')}
                      size="small"
                      className={filterStatus === 'failed' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      失败
                    </Button>
                  </div>
                </div>
              ) : (
                /* 桌面端：原有布局 */
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="flex items-center gap-3">
                  <Text strong className="text-gray-700 text-responsive-sm">类型</Text>
                  <Space size="small">
                    <Button
                      type={activeTab === 'all' ? 'primary' : 'default'}
                      onClick={() => setActiveTab('all')}
                      size="small"
                      className={activeTab === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      全部
                    </Button>
                    <Button
                      type={activeTab === 'video' ? 'primary' : 'default'}
                      icon={<VideoCameraOutlined />}
                      onClick={() => setActiveTab('video')}
                      size="small"
                      className={activeTab === 'video' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      视频
                    </Button>
                    <Button
                      type={activeTab === 'image' ? 'primary' : 'default'}
                      icon={<PictureOutlined />}
                      onClick={() => setActiveTab('image')}
                      size="small"
                      className={activeTab === 'image' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      图像
                    </Button>
                  </Space>
                </div>
                
                <div className="flex items-center gap-3">
                  <Text strong className="text-gray-700 text-responsive-sm">状态</Text>
                  <Space size="small">
                    <Button
                      type={filterStatus === 'all' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('all')}
                      size="small"
                      className={filterStatus === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      全部
                    </Button>
                      <Button
                        type={filterStatus === 'processing' ? 'primary' : 'default'}
                        onClick={() => setFilterStatus('processing')}
                        size="small"
                        className={filterStatus === 'processing' ? '' : 'border-gray-300'}
                        style={{ 
                          borderRadius: '20px', 
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          transition: 'none'
                        }}
                      >
                        处理中
                      </Button>
                    <Button
                      type={filterStatus === 'completed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('completed')}
                      size="small"
                      className={filterStatus === 'completed' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      已完成
                    </Button>
                    <Button
                      type={filterStatus === 'failed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('failed')}
                      size="small"
                      className={filterStatus === 'failed' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      失败
                    </Button>
                  </Space>
                </div>
              </div>
              )}
              
              {/* 右侧：搜索 + 统计 */}
              <div className="flex flex-row items-center gap-3 md:gap-4 w-full md:w-auto md:justify-end">
                {/* 搜索框 - 重新设计 */}
                <div className="history-search-wrapper" style={{
                  maxWidth: isMobile ? '100%' : 280,
                  width: isMobile ? '100%' : 'auto',
                  flex: isMobile ? 1 : 'none',
                  position: 'relative',
                }}>
                  <SearchOutlined className="history-search-icon" />
                  <Input
                     allowClear 
                    placeholder="搜索提示词、模型或ID..."
                    value={searchKeyword}
                    onChange={(e) => {
                      const value = e.target.value
                      setSearchKeyword(value)
                    }}
                    onPressEnter={(e) => {
                      const value = (e.target as HTMLInputElement).value
                      setSearchKeyword(value)
                    }}
                    className="history-search-input-new"
                  />
               </div>
                <Text
                  strong
                  className="text-gray-700 text-responsive-sm flex-shrink-0"
                  style={{ 
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                    lineHeight: '1.5',
                    paddingLeft: isMobile ? '0' : '8px',
                    fontWeight: 500
                  }}
                >
                  共{' '}
                  <span className="text-blue-600 font-bold" style={{ 
                    fontSize: 'inherit',
                    fontWeight: 700,
                    letterSpacing: '0.02em'
                  }}>
                    {activeTab === 'video'
                      ? videoTotal
                      : activeTab === 'image'
                      ? imageTotal
                      : videoTotal + imageTotal}
                  </span>{' '}
                  条记录
                </Text>
            </div>
            </div>
          </Card>

          {/* 内容列表 */}
          <Card 
            className="unified-card border border-gray-100 history-card"
            styles={{ 
              body: { 
                padding: 0,
              } 
            }}
          >
            {/* 顶部装饰渐变线 */}
            <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1 w-full"></div>

            {/* 为列表区域设置最小高度，避免筛选切换时卡片高度剧烈变化导致页面抖动 */}
            <div style={{ minHeight: 320 }}>
            {isLoading && data.length === 0 ? (
                // 初始加载时显示骨架屏
                <div style={{ padding: '12px' }}>
                  {Array.from({ length: limit }).map((_, index) => (
                    <div key={`skeleton-${index}`} style={{ marginBottom: 'var(--spacing-lg)' }}>
                      {renderSkeletonItem()}
               </div>
                  ))}
                  </div>
              ) : filteredHistory.length === 0 ? (
                <div style={{ padding: '40px 12px' }}>
                  <Empty
                    description={
                      <div>
                        <Text className="text-gray-500 text-responsive-sm">
                          {searchKeyword.trim() ? `未找到匹配"${searchKeyword}"的记录` : '暂无生成记录'}
                        </Text>
                        <br />
                        <Text className="text-gray-400 text-xs mt-2">
                          {searchKeyword.trim() 
                            ? '请尝试调整搜索关键词或清除搜索条件'
                            : '开始创作您的第一个AI作品吧'}
                        </Text>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                     />
                  </div>
              ) : (
                <List
                  dataSource={searchKeyword.trim() ? filteredHistory.slice((page - 1) * limit, page * limit) : data}
                  loading={isLoading && data.length > 0}
                  split={false}
                  style={{ transition: 'none' }}
                  pagination={{ 
                    current: page,
                    pageSize: limit,
                    // 🔍 使用过滤后的数据总数（前端搜索）或后端返回的总数（无搜索）
                    total: searchKeyword.trim() ? filteredHistory.length : paginationTotal,
                    showSizeChanger: !isMobile && !searchKeyword.trim(), // 搜索时隐藏每页条数选择器
                    pageSizeOptions: ['10','20'],
                    showQuickJumper: !searchKeyword.trim(), // 搜索时隐藏快速跳转
                    showLessItems: isMobile, // 移动端仅保留前后少量页码
                    onChange: (p) => {
                      // 🔍 根据是否有搜索关键词计算总页数
                      const total = searchKeyword.trim() ? filteredHistory.length : paginationTotal
                      const totalPages = Math.max(1, Math.ceil(total / limit))
                      const next = Math.min(Math.max(1, p), totalPages)
                      if (next !== page) setPage(next)
                      // 立即滚动到列表顶部，不使用平滑滚动
                      try { window.scrollTo({ top: 0, behavior: 'auto' }) } catch {}
                    },
                    onShowSizeChange: (p, size) => { 
                      if (!searchKeyword.trim()) {
                        setPage(1)
                        setLimit(size)
                      }
                      try { window.scrollTo({ top: 0, behavior: 'auto' }) } catch {} 
                    },
                    showTotal: isMobile ? undefined : (total, range) => {
                      if (searchKeyword.trim()) {
                        return `搜索到 ${total} 条记录`
                      }
                      return `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`
                    },
                    // 移动端不使用 simple 模式，显示完整分页按钮
                    style: { textAlign: 'center', padding: '12px 0' },
                    className: 'history-pagination'
                  }}
                  renderItem={(item, index) => {
                    // 使用 useCallback 优化渲染函数
                    // 为前6个可见项添加索引，用于优化图片加载
                    const itemWithIndex = { ...item, index: (page - 1) * limit + index }
                    const isLast =
                      index ===
                      ((searchKeyword.trim() ? filteredHistory.slice((page - 1) * limit, page * limit) : data) as any[]).length -
                        1
                    return (
                      <List.Item className="!px-0 !border-0 history-list-item">
                        <div className="w-full">
                          {item.type === 'video' ? renderVideoItem(itemWithIndex) : renderImageItem(itemWithIndex)}
                        </div>
                        {!isLast && (
                          <div className="history-item-divider">
                            <div className="history-item-divider-line" />
                          </div>
                        )}
                      </List.Item>
                    )
                  }}
                />
              )}
            </div>
          </Card>
        </div>
         </div>

      {/* 视频预览Modal - 沉浸式体验 */}
         <Modal
            open={videoPreviewModal.visible}
        onCancel={handleCloseVideoPreview}
            footer={null}
        width="auto"
            centered
        destroyOnClose
        maskClosable
        className="preview-modal-immersive"
        styles={{
          body: { 
            padding: '24px',
            background: 'transparent'
          },
          content: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0
          },
          mask: {
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(168, 85, 247, 0.3) 50%, rgba(236, 72, 153, 0.3) 100%), rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }
        }}
        closeIcon={
          <div className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/60 transition-all cursor-pointer border border-white/20">
            <CloseOutlined className="text-lg" />
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center gap-4">
          {/* 视频播放器 */}
          <div className="relative">
                  <video 
                     controls 
                     autoPlay 
              playsInline
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
              style={{
                maxWidth: 'min(90vw, 1920px)',
                maxHeight: 'min(70vh, 1080px)',
                backgroundColor: '#000'
              }}
            >
              <source src={videoPreviewModal.videoUrl} type="video/mp4" />
              您的浏览器不支持视频播放。
            </video>
               </div>
          
          {/* 底部操作按钮 */}
          <div className="flex items-center justify-center gap-3 mt-2">
                     <Button 
                        type="primary" 
              size={isMobile ? 'middle' : 'large'}
                        icon={<DownloadOutlined />} 
              onClick={() => {
                handleDownload(videoPreviewModal.videoUrl, `${videoPreviewModal.prompt || 'video'}.mp4`, true)
              }}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 border-0 font-bold shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50"
              style={{
                borderRadius: '12px'
              }}
            >
              {isMobile ? '下载' : '下载视频'}
            </Button>
            <Button 
              size={isMobile ? 'middle' : 'large'}
              onClick={handleCloseVideoPreview}
              className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20"
              style={{
                borderRadius: '12px'
              }}
            >
              关闭
                     </Button>
               </div>
            </div>
         </Modal>

      {/* 图片预览Modal - 沉浸式体验 */}
         <Modal
            open={imagePreviewModal.visible}
        onCancel={handleCloseImagePreview}
            footer={null}
        width="auto"
            centered
        destroyOnClose
        maskClosable
        className="preview-modal-immersive"
        styles={{
          body: { 
            padding: '24px',
            background: 'transparent'
          },
          content: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0
          },
          mask: {
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(168, 85, 247, 0.3) 50%, rgba(236, 72, 153, 0.3) 100%), rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }
        }}
        closeIcon={
          <div className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/60 transition-all cursor-pointer border border-white/20">
            <CloseOutlined className="text-lg" />
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center gap-4">
          {/* 图片显示区域 */}
          <div className="relative">
            {imagePreviewModal.imageUrls.length === 1 ? (
              // 单张图片
              <img
                src={imagePreviewModal.imageUrls[0]}
                alt="预览图片"
                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
                style={{
                  maxWidth: 'min(90vw, 2560px)',
                  maxHeight: 'min(70vh, 1440px)'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="16"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                }}
              />
            ) : (
              // 多张图片 - 横向滚动
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center justify-center overflow-x-auto pb-2 w-full max-w-[90vw]">
                  <div className="flex gap-4 items-center px-4">
                    {imagePreviewModal.imageUrls.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`图片 ${index + 1}`}
                        className={`cursor-pointer rounded-lg shadow-xl transition-all ${
                          index === 0 ? 'ring-2 ring-violet-500 scale-105' : 'opacity-70 hover:opacity-100'
                        }`}
                        style={{
                          maxWidth: 'min(80vw, 1200px)',
                          maxHeight: 'min(60vh, 800px)',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain'
                        }}
                        onClick={(e) => {
                          // 点击切换为主图
                          const newUrls = [...imagePreviewModal.imageUrls];
                          [newUrls[0], newUrls[index]] = [newUrls[index], newUrls[0]];
                          setImagePreviewModal({ ...imagePreviewModal, imageUrls: newUrls });
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ))}
                        </div>
                        </div>
                {/* 图片计数提示 */}
                <div className="text-white/80 text-sm bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                  共 {imagePreviewModal.imageUrls.length} 张图片，点击切换查看
                </div>
              </div>
                  )}
               </div>

          {/* 底部操作按钮 */}
          <div className="flex items-center justify-center gap-3 mt-2">
                     <Button 
                        type="primary" 
              size={isMobile ? 'middle' : 'large'}
                        icon={<DownloadOutlined />} 
              onClick={() => {
                // 下载所有图片 - 使用统一的 loading
                const batchLoadingKey = `preview_batch_download_${Date.now()}`
                message.loading({ 
                  content: `正在准备下载 ${imagePreviewModal.imageUrls.length} 张图片...`, 
                  key: batchLoadingKey, 
                  duration: 0 
                })
                
                let completedCount = 0
                const totalCount = imagePreviewModal.imageUrls.length
                
                imagePreviewModal.imageUrls.forEach((url, index) => {
                  setTimeout(async () => {
                    try {
                      // 批量下载时跳过单个文件的 loading 提示
                      await handleDownload(url, `${imagePreviewModal.prompt || 'image'}_${index + 1}.jpg`, false, true)
                      completedCount++
                      
                      if (completedCount === totalCount) {
                        message.destroy(batchLoadingKey)
                        message.success(`已开始下载 ${totalCount} 张图片`)
                      }
                    } catch (error) {
                      completedCount++
                      if (completedCount === totalCount) {
                        message.destroy(batchLoadingKey)
                      }
                    }
                  }, index * 500)
                })
              }}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 border-0 font-bold shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50"
              style={{
                borderRadius: '12px'
              }}
            >
              {isMobile ? '下载' : `下载${imagePreviewModal.imageUrls.length > 1 ? `全部(${imagePreviewModal.imageUrls.length})` : ''}`}
            </Button>
            <Button 
              size={isMobile ? 'middle' : 'large'}
              onClick={handleCloseImagePreview}
              className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20"
              style={{
                borderRadius: '12px'
              }}
            >
              关闭
                     </Button>
                  </div>
        </div>
      </Modal>
      
      {/* 🔥 创建角色Modal */}
      <Modal
        title="从原视频创建角色"
        open={createCharacterModal.visible}
        onCancel={() => setCreateCharacterModal({
          visible: false,
          videoItem: null,
          characterName: '',
          timestamps: '0,3',
          loading: false
        })}
        onOk={handleCreateCharacter}
        confirmLoading={createCharacterModal.loading}
        okText="创建角色"
        cancelText="取消"
        width={isMobile ? '90%' : 500}
      >
        <div style={{ padding: '16px 0' }}>
          <Paragraph>
            从已完成的视频中提取角色，创建后可重复使用。
          </Paragraph>
          
          <div style={{ marginBottom: '16px' }}>
            <Text strong>角色名称：</Text>
            <Input
              value={createCharacterModal.characterName}
              onChange={(e) => setCreateCharacterModal(prev => ({ ...prev, characterName: e.target.value }))}
              placeholder="请输入角色名称"
              style={{ marginTop: '8px' }}
            />
                           </div>
          
          <div style={{ marginBottom: '16px' }}>
            <Text strong>时间范围（秒）：</Text>
            <Input
              value={createCharacterModal.timestamps}
              onChange={(e) => setCreateCharacterModal(prev => ({ ...prev, timestamps: e.target.value }))}
              placeholder="0,3"
              style={{ marginTop: '8px' }}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#999' }}>
              格式：开始秒数,结束秒数（例如：0,3 表示从第0秒到第3秒，最多3秒）
                     </div>
          </div>
          
          {createCharacterModal.videoItem && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                视频ID: {createCharacterModal.videoItem.id}<br />
                {createCharacterModal.videoItem.pid ? (
                  <>PID: {createCharacterModal.videoItem.pid}</>
                ) : (
                  <>视频URL: {createCharacterModal.videoItem.result ? '已提供' : '未提供'}</>
                )}
              </Text>
               </div>
          )}
            </div>
         </Modal>
      </div>
  )
}

export default HistoryPage
